/**
 * Human-readable label parts for a saved/recent report configuration, built
 * from the request params that generated it (reportRequest.js shape): which
 * portfolio it targets and the report date(s), plus a compact generated-at
 * stamp. Used by the Saved/Recent rail so entries are identifiable at a
 * glance instead of showing only the report name.
 */

/**
 * '@@' for an aggregate (consolidated) group run, '@' for per-account.
 * The one place the sigil is minted for display - derive the mode from the
 * REQUEST (params.group_mode) or from the envelope's group.consolidated, never
 * from the envelope's mode field: three report types force the aggregate path
 * even when the user typed a single '@'.
 */
export function groupSigil(aggregate) {
  const v = typeof aggregate === 'string' ? aggregate.toLowerCase() === 'aggregate' : Boolean(aggregate);
  return v ? '@@' : '@';
}

/** "MUTELY", "@REDACTED-CLIENT" / "@@REDACTED-CLIENT" (group), or account fallback - '' when unknown. */
export function configScope(params) {
  const p = params || {};
  if (p.group_codename) {
    return `${groupSigil(p.group_mode)}${String(p.group_codename).toUpperCase()}`;
  }
  return (
    p.account_name || p.client_codename || p.client_name || p.account_number || ''
  );
}

/** "as of 2026-07-15" or "2026-01-01 to 2026-07-15" - '' when unknown. */
export function configDates(params) {
  const p = params || {};
  if (p.date_range && p.date_range.start && p.date_range.end) {
    return `${p.date_range.start} to ${p.date_range.end}`;
  }
  return p.date ? `as of ${p.date}` : '';
}

/** " · "-joined scope + dates, e.g. "MUTELY · as of 2026-07-15". */
export function configScopeLabel(params) {
  return [configScope(params), configDates(params)].filter(Boolean).join(' · ');
}

/** Compact local stamp for an epoch-ms time, e.g. "7/17, 4:12 AM". */
export function formatStamp(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
